/**
 * История изменений элемента.
 *
 * Показывает, кто и что изменил. Восстановление прошлой версии в первой
 * версии не предусмотрено (документ «Итоговое описание», §14.1) — сначала
 * нужно понять, требуется ли оно на практике.
 */

import { HistoryIcon } from 'lucide-react';
import type { RevisionChangeDto } from '@project-os/contracts';
import { useItemRevisions } from '@/api/collaboration';
import { EmptyState, ErrorState } from '@/components/common/states';
import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/common/user-avatar';
import { formatDateTime, formatRelative } from '@/lib/format';

export function RevisionsSection({ projectId, itemId }: { projectId: string; itemId: string }) {
  const { data, isPending, isError, error, refetch } = useItemRevisions(projectId, itemId);

  if (isPending) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  if (data.length === 0) {
    return (
      <EmptyState
        icon={<HistoryIcon />}
        title="Элемент ещё не изменялся"
        description="Здесь появятся редакции: кто, когда и какие поля изменил. Запись создаётся только при значимом изменении."
      />
    );
  }

  return (
    <ol className="space-y-3">
      {data.map((revision) => (
        <li key={revision.id} className="rounded-md border px-3 py-2.5">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <UserAvatar user={revision.createdBy} size="sm" />
            <span className="text-sm font-medium">{revision.createdBy.name}</span>
            <span
              className="text-2xs text-muted-foreground"
              title={formatDateTime(revision.createdAt)}
            >
              {formatRelative(revision.createdAt)}
            </span>
            <span className="ml-auto text-2xs text-muted-foreground/70 tabular-nums">
              редакция {revision.revisionNumber}
            </span>
          </div>

          <ul className="mt-2 space-y-1.5">
            {revision.changes.map((change) => (
              <ChangeRow key={change.field} change={change} />
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
}

function ChangeRow({ change }: { change: RevisionChangeDto }) {
  return (
    <li className="text-xs">
      <span className="text-muted-foreground">{change.labelRu}: </span>
      <span className="text-muted-foreground/80 line-through">{renderValue(change.before)}</span>
      <span className="mx-1.5 text-muted-foreground">→</span>
      <span className="text-foreground">{renderValue(change.after)}</span>
    </li>
  );
}

/**
 * Значения показываются кратко: история должна читаться взглядом, а не
 * превращаться в дамп объектов. Длинный текст обрезается.
 */
function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'не задано';
  if (typeof value === 'boolean') return value ? 'да' : 'нет';
  if (Array.isArray(value)) return value.length === 0 ? 'пусто' : `${value.length} знач.`;
  if (typeof value === 'object') return 'объект';

  const text = String(value);
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
}
